import { Download, HardDrive, Trash2, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs.js';
import { formatDateTime } from '../../scenario/utils.js';
import { CreateBackupDialog } from '../components/CreateBackupDialog.js';
import { CreateRestoreDialog } from '../components/CreateRestoreDialog.js';
import {
  useBackupJobs,
  useDeletePlanningBackup,
  useDeletePlanningRestore,
  useRestoreJobs,
  type PlanningBackupJob,
  type PlanningRestoreJob,
} from '../hooks.js';

/**
 * Planning · Backup & Restore. Two tabs:
 *   - **Backups** — history table of every `backups` row for the
 *     license, with a "Trigger backup" CTA opening `CreateBackupDialog`.
 *     Completed runs surface a download link to the S3 archive.
 *   - **Restores** — history table of every `backup_restores` row,
 *     with a "Trigger restore" CTA. Rows can deep-link a restore back
 *     into the dialog via the per-row "Restore again" button (passes
 *     backup id pre-selected).
 *
 * URL routing: the page is mounted at both `/planning/backup` and
 * `/planning/restore` (sidebar has separate nav links for each). The
 * URL drives the initial tab — `/planning/restore` opens on the
 * Restores tab. Clicking a tab updates the URL via `useNavigate`, so
 * the sidebar's active highlight + the deep-link semantics stay
 * symmetric.
 *
 * Polling: every 5 s while a job in either tab is still in flight,
 * relaxed to 30 s once everything settles. Mirrors the live-refresh
 * UX of the legacy backup-restore page.
 */
export function PlanningBackupPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  // Derive the active tab from the URL on every render so the
  // sidebar nav (different routes) and in-page tab clicks (same
  // route, different `value`) stay in sync. Default to `backups` for
  // any unrecognised pathname so a typo doesn't strand the user on
  // an empty tab.
  const activeTab: 'backups' | 'restores' =
    location.pathname === '/planning/restore' ? 'restores' : 'backups';

  const [createBackupOpen, setCreateBackupOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreBackupId, setRestoreBackupId] = useState<string>('');
  // Per-row delete confirm state. Discriminated by `kind` so the
  // confirm modal can render the right copy + dispatch to the right
  // mutation. `null` = closed.
  const [deleting, setDeleting] = useState<
    { kind: 'backup'; row: PlanningBackupJob } | { kind: 'restore'; row: PlanningRestoreJob } | null
  >(null);

  const deleteBackup = useDeletePlanningBackup();
  const deleteRestore = useDeletePlanningRestore();

  const backups = useBackupJobs();
  const restores = useRestoreJobs();

  // Tighten polling whenever either feed has an in-flight row.
  const anyInFlight = useMemo(() => {
    const hasInFlight = (rows: ReadonlyArray<{ status: string }>): boolean =>
      rows.some((r) => isPendingStatus(r.status));
    return hasInFlight(backups.data ?? []) || hasInFlight(restores.data ?? []);
  }, [backups.data, restores.data]);

  // Re-arm the underlying queries with the right interval. Hooks
  // expose `refetch()` so this works without re-creating the query.
  const liveBackups = useBackupJobs({ refetchIntervalMs: anyInFlight ? 5_000 : 30_000 });
  const liveRestores = useRestoreJobs({ refetchIntervalMs: anyInFlight ? 5_000 : 30_000 });

  const backupRows = liveBackups.data ?? backups.data ?? [];
  const restoreRows = liveRestores.data ?? restores.data ?? [];

  const openRestoreFor = (backupId: string): void => {
    setRestoreBackupId(backupId);
    setRestoreOpen(true);
  };

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Planning · Backup &amp; Restore</h1>
        <p className="text-sm text-foreground/60">
          Trigger Workfront-Planning backups or restores. The Fargate consumer runs in the
          background and updates the history below as it works.
        </p>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          // Keep the URL in sync so the sidebar's active highlight
          // tracks the tab + the page stays deep-linkable.
          navigate(v === 'restores' ? '/planning/restore' : '/planning/backup', { replace: true });
        }}
        className="px-6 pt-3"
      >
        <TabsList className="bg-foreground/[0.04]">
          <TabsTrigger value="backups">Backups ({backupRows.length})</TabsTrigger>
          <TabsTrigger value="restores">Restores ({restoreRows.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="backups" className="space-y-3">
          <div className="flex items-center justify-between pt-3">
            <p className="text-xs text-foreground/50">
              {anyInFlight
                ? 'Auto-refreshing every 5 s while a job is in flight.'
                : 'Refreshes every 30 s in the background.'}
            </p>
            <Button
              type="button"
              onClick={() => setCreateBackupOpen(true)}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              <HardDrive className="mr-1.5 h-4 w-4" /> Trigger backup
            </Button>
          </div>
          <JobHistoryTable
            rows={backupRows}
            isLoading={liveBackups.isLoading && !liveBackups.data}
            error={liveBackups.error}
            kind="backup"
            onRestoreFromBackup={openRestoreFor}
            onDelete={(row) => setDeleting({ kind: 'backup', row: row as PlanningBackupJob })}
          />
        </TabsContent>

        <TabsContent value="restores" className="space-y-3">
          <div className="flex items-center justify-between pt-3">
            <p className="text-xs text-foreground/50">
              {anyInFlight
                ? 'Auto-refreshing every 5 s while a job is in flight.'
                : 'Refreshes every 30 s in the background.'}
            </p>
            <Button
              type="button"
              onClick={() => {
                setRestoreBackupId('');
                setRestoreOpen(true);
              }}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              <Upload className="mr-1.5 h-4 w-4" /> Trigger restore
            </Button>
          </div>
          <JobHistoryTable
            rows={restoreRows}
            isLoading={liveRestores.isLoading && !liveRestores.data}
            error={liveRestores.error}
            kind="restore"
            onDelete={(row) => setDeleting({ kind: 'restore', row: row as PlanningRestoreJob })}
          />
        </TabsContent>
      </Tabs>

      <CreateBackupDialog
        open={createBackupOpen}
        onOpenChange={setCreateBackupOpen}
        onTriggered={() => void liveBackups.refetch()}
      />
      <CreateRestoreDialog
        open={restoreOpen}
        onOpenChange={(o) => {
          setRestoreOpen(o);
          if (!o) setRestoreBackupId('');
        }}
        initialBackupId={restoreBackupId}
        onTriggered={() => void liveRestores.refetch()}
      />

      {deleting ? (
        <DeleteConfirmDialog
          kind={deleting.kind}
          row={deleting.row}
          submitting={deleting.kind === 'backup' ? deleteBackup.isPending : deleteRestore.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            if (deleting.kind === 'backup') {
              await deleteBackup.remove(deleting.row.id);
              setDeleting(null);
              void liveBackups.refetch();
            } else {
              await deleteRestore.remove(deleting.row.id);
              setDeleting(null);
              void liveRestores.refetch();
            }
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Inline delete-confirm modal. Two flavors — backup or restore — so
 * the copy can call out that backup-delete also drops the S3
 * archive. Pattern mirrors the KPI page's DeleteConfirmDialog
 * (kept inline because both are single-consumer today).
 */
function DeleteConfirmDialog({
  kind,
  row,
  submitting,
  onCancel,
  onConfirm,
}: {
  kind: 'backup' | 'restore';
  row: PlanningBackupJob | PlanningRestoreJob;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}): JSX.Element {
  const created = formatDateTime(row.createdAt);
  const subtitle =
    kind === 'backup'
      ? 'This drops the history row and removes the S3 archive. It cannot be undone.'
      : 'This drops the restore history row. The originating backup is not affected.';
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
    >
      <div
        className="max-w-md rounded-md border border-foreground/15 bg-[color:var(--surface-overlay)] p-6 text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-medium">Delete this {kind}?</h3>
        <p className="mt-2 text-sm text-foreground/70">
          <span className="font-mono text-xs">{row.instance}</span> · {created}
        </p>
        <p className="mt-2 text-sm text-foreground/70">{subtitle}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="border-foreground/15 text-foreground/70"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void onConfirm()}
            disabled={submitting}
            className="bg-rose-500 text-white hover:bg-rose-600"
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── History table — handles both backup + restore rows ── */
interface BackupRowFields {
  id: string;
  status: string;
  instance: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  fileUrl?: string | null;
  sourceUrl?: string;
}

function JobHistoryTable({
  rows,
  isLoading,
  error,
  kind,
  onRestoreFromBackup,
  onDelete,
}: {
  rows: ReadonlyArray<PlanningBackupJob | PlanningRestoreJob>;
  isLoading: boolean;
  error: Error | null;
  kind: 'backup' | 'restore';
  onRestoreFromBackup?: (backupId: string) => void;
  onDelete?: (row: PlanningBackupJob | PlanningRestoreJob) => void;
}): JSX.Element {
  if (error) {
    return (
      <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
        Could not load {kind} jobs: {error.message}
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
        Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
        No {kind} jobs recorded yet. Trigger one to see it land here.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-foreground/10 bg-black/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-foreground/10 text-xs uppercase tracking-wide text-foreground/40">
            <th className="px-3 py-2 text-left font-medium">Created</th>
            <th className="px-3 py-2 text-left font-medium">Started</th>
            <th className="px-3 py-2 text-left font-medium">Finished</th>
            <th className="px-3 py-2 text-left font-medium">Instance</th>
            <th className="px-3 py-2 text-left font-medium">Status</th>
            <th className="px-3 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="text-foreground/85">
          {rows.map((row) => {
            const r = row as BackupRowFields;
            return (
              <tr key={r.id} className="border-b border-foreground/5 align-top">
                <td className="px-3 py-2 text-xs">{formatDateTime(r.createdAt)}</td>
                <td className="px-3 py-2 text-xs">{formatDateTime(r.startedAt ?? undefined)}</td>
                <td className="px-3 py-2 text-xs">{formatDateTime(r.finishedAt ?? undefined)}</td>
                <td className="px-3 py-2 font-mono text-xs text-foreground/70">{r.instance}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex rounded px-2 py-0.5 text-xs ${statusBadgeClass(r.status)}`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-3">
                    {kind === 'backup' && r.fileUrl ? (
                      <>
                        <a
                          href={r.fileUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          title="Open backup archive"
                          className="inline-flex items-center gap-1 text-foreground/70 hover:text-foreground"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                        {onRestoreFromBackup ? (
                          <button
                            type="button"
                            onClick={() => onRestoreFromBackup(r.id)}
                            className="text-xs text-foreground/60 underline hover:text-foreground"
                          >
                            Restore from this
                          </button>
                        ) : null}
                      </>
                    ) : kind === 'restore' && r.sourceUrl ? (
                      <span className="font-mono text-xs text-foreground/40" title={r.sourceUrl}>
                        {r.sourceUrl.split('/').slice(-2).join('/')}
                      </span>
                    ) : (
                      <span className="text-xs text-foreground/30">—</span>
                    )}
                    {onDelete ? (
                      <button
                        type="button"
                        onClick={() => onDelete(row)}
                        disabled={r.status.toLowerCase().includes('deleting')}
                        title={`Delete ${kind}`}
                        aria-label={`Delete ${kind}`}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-foreground/40 hover:bg-rose-500/10 hover:text-rose-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-foreground/40"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function isPendingStatus(status: string): boolean {
  const s = status.toLowerCase();
  return (
    s.includes('running') ||
    s.includes('progress') ||
    s.includes('queued') ||
    s.includes('pending') ||
    s.includes('start') ||
    // `Deleting` is the optimistic local status that
    // `useDeletePlanningBackup` / `useDeletePlanningRestore` flips a
    // row to while the engine commits the DELETE + S3 cleanup. Same
    // tightened-polling treatment as other in-flight statuses so the
    // row disappears within ~5 s once the engine confirms.
    s.includes('deleting') ||
    s === 'created'
  );
}

function statusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('success') || s.includes('complete') || s === 'ok' || s === 'done')
    return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300';
  if (s.includes('fail') || s.includes('error'))
    return 'bg-rose-500/20 text-rose-700 dark:text-rose-300';
  // Deleting reads as an in-flight destructive action — rose-tinted
  // amber so it stands out from the "running" amber of normal jobs.
  if (s.includes('deleting')) return 'bg-rose-500/15 text-rose-700 dark:text-rose-300';
  if (s.includes('progress') || s.includes('running'))
    return 'bg-amber-500/20 text-amber-700 dark:text-amber-300';
  if (s.includes('queued') || s.includes('pending') || s === 'created')
    return 'bg-sky-500/20 text-sky-700 dark:text-sky-300';
  return 'bg-foreground/10 text-foreground/70';
}
